import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { usePageHeader } from '../../components/Layout';
import { Avatar, Chip, SectionCard } from '../../design/primitives';
import { Ic } from '../../design/icons';

const API = `${window.location.origin}/api`;

const ACTION_LABELS = {
  'super_admin.user_update': 'Kullanıcı Güncelleme',
  'super_admin.user_promote': 'Süper Admin Yapma',
  'super_admin.user_demote': 'Süper Admin Düşürme',
  'super_admin.workspace_update': 'Workspace Güncelleme',
  'super_admin.campaign_send': 'Kampanya Gönderimi',
};

function formatTry(kurus) {
  if (kurus == null) return '₺0';
  const tl = (kurus / 100).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  return `₺${tl}`;
}

function formatRelative(iso) {
  if (!iso) return '';
  const diffMin = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (diffMin < 1) return 'Az önce';
  if (diffMin < 60) return `${diffMin} dk önce`;
  const h = Math.floor(diffMin / 60);
  if (h < 24) return `${h} sa önce`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} gün önce`;
  return new Date(iso).toLocaleDateString('tr-TR');
}

export default function SuperAdminDashboard() {
  usePageHeader([{ label: 'Süper Admin' }, { label: 'Pano' }], null, []);

  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/super-admin/overview`);
        if (active) setOverview(res.data);
      } catch (err) {
        if (active) toast.error(err?.response?.data?.detail || 'Pano verileri yüklenemedi');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const totals = overview?.totals || {};
  const recentCampaigns = overview?.recentCampaigns || [];
  const recentAudit = overview?.recentAudit || [];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tiles = useMemo(() => ([
    { label: 'Toplam Kullanıcı', value: totals.users ?? '—' },
    { label: 'Süper Adminler', value: totals.superAdmins ?? '—', color: '#b45309' },
    { label: "Workspace'ler", value: totals.workspaces ?? '—', color: 'var(--accent)' },
    { label: 'Aktif Abonelik', value: totals.activeSubscriptions ?? '—', color: 'var(--good)' },
    { label: 'MRR', value: formatTry(totals.mrrKurus ?? 0), color: 'var(--accent)', small: true },
    { label: 'Kampanya (30g)', value: totals.campaigns30d ?? 0 },
  ]), [totals]);

  return (
    <div className="ox-page space-y-4" style={{ padding: 24 }}>
      <div className="ox-grid-stats-6">
        {tiles.map((t) => (
          <div key={t.label} className="ox-surface rounded-md p-3">
            <div className="text-[10.5px] ox-text-muted uppercase tracking-wider">{t.label}</div>
            <div
              className="ox-tnum font-semibold mt-1"
              style={{ color: t.color || 'var(--text)', fontSize: t.small ? 18 : 22 }}
            >
              {loading ? '…' : t.value}
            </div>
          </div>
        ))}
      </div>

      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        <SectionCard title="Hızlı erişim">
          <div className="flex flex-col gap-2">
            <QuickLink to="/super-admin/users" icon={Ic.users} label="Tüm Kullanıcılar" hint="Filtrele, rol/status düzenle" />
            <QuickLink to="/super-admin/workspaces" icon={Ic.projects} label="Tüm Workspace'ler" hint="Plan/durum, üye geçişi" />
            <QuickLink to="/super-admin/campaigns" icon={Ic.bell} label="E-posta Kampanyaları" hint="Hedef kitleye toplu mail" />
            <QuickLink to="/super-admin/audit" icon={Ic.audit} label="Süper Admin Günlüğü" hint="İşlem denetim izi" />
          </div>
        </SectionCard>

        <SectionCard title="Son kampanyalar">
          {loading ? (
            <div className="text-[12.5px] ox-text-muted">Yükleniyor...</div>
          ) : recentCampaigns.length === 0 ? (
            <div className="text-[12.5px] ox-text-muted">Henüz kampanya gönderilmedi.</div>
          ) : (
            <div className="space-y-2">
              {recentCampaigns.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center justify-between gap-2 p-2 rounded"
                  style={{ background: 'var(--surface-muted)' }}
                >
                  <div className="min-w-0">
                    <div className="text-[12.5px] font-medium truncate">{c.subject}</div>
                    <div className="text-[11px] ox-text-muted truncate">
                      {c.audienceType} · {c.recipientCount || 0} alıcı
                    </div>
                  </div>
                  <Chip
                    color={c.status === 'completed' ? 'var(--good)' : c.status === 'failed' ? 'var(--danger)' : 'var(--accent)'}
                    bg="var(--surface)"
                  >
                    {c.status}
                  </Chip>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard title="Son süper admin işlemleri" style={{ gridColumn: 'span 2' }}>
          {loading ? (
            <div className="text-[12.5px] ox-text-muted">Yükleniyor...</div>
          ) : recentAudit.length === 0 ? (
            <div className="text-[12.5px] ox-text-muted">Henüz süper admin işlemi yok.</div>
          ) : (
            <div className="space-y-1.5">
              {recentAudit.map((l) => (
                <div
                  key={l.id}
                  className="flex items-start gap-2 p-2 rounded"
                  style={{ background: 'var(--surface-muted)' }}
                >
                  <Avatar u={{ id: l.userId, name: l.userName }} size={22} />
                  <div className="min-w-0 flex-1">
                    <div className="text-[12.5px]" style={{ color: 'var(--text)' }}>
                      <span className="font-medium">{l.userName || '—'}</span>
                      <span className="ox-text-muted"> · {ACTION_LABELS[l.action] || l.action}</span>
                    </div>
                    <div className="text-[11.5px] ox-text-muted truncate">{l.details}</div>
                  </div>
                  <div className="text-[11px] ox-text-muted whitespace-nowrap">{formatRelative(l.createdAt)}</div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}

function QuickLink({ to, icon, label, hint }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 p-2 rounded"
      style={{ background: 'var(--surface-muted)', textDecoration: 'none', color: 'var(--text)' }}
    >
      <span style={{ color: 'var(--accent)' }}>{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-medium">{label}</div>
        <div className="text-[11px] ox-text-muted">{hint}</div>
      </div>
      <span className="ox-text-dim">{Ic.chevronRight || '›'}</span>
    </Link>
  );
}
